import { groupBy } from 'lodash-es';
import { FilterResourceType } from '@features/components/CaseOverview/components/AssignedResources/types';
import { mapUsersIdsByRoles } from '@features/components/CaseOverview/components/AssignedResources/utils';
import {
  AssignedOpoContactType,
  AssignedUsersByGroup,
  CaseResourceCombined,
  CaseResourceType,
  ResourceLogisticsStatus,
} from '@/api/apis/case';
import { Employee, EmployeeRole } from '@/api/apis/resources';
import { ActiveResourceType, ResourceFilters, resourceTypeIs } from './config';

export const candidatesWithScheduler = [
  EmployeeRole.ClinicalSpecialist,
  EmployeeRole.RetrievalSurgeon,
  EmployeeRole.ClinicalCoordinator,
  EmployeeRole.ClinicalLead,
  EmployeeRole.OrganManagementSpecialist,
];

export const mapTMDXEmployees = (
  employees: Employee[],
  employeesToAssign: Partial<Record<EmployeeRole, string[]>>,
  selectedRole: EmployeeRole,
) => {
  return employees.map(
    ({
      id,
      userId,
      firstName,
      lastName,
      profilePhoto,
      phone,
      businessPhone,
      roles,
      organSpecializations,
      baseLocationObject,
    }) => {
      return {
        id,
        userId,
        firstName,
        lastName,
        photoPath: profilePhoto,
        primaryPhone: phone,
        secondaryPhone: businessPhone,
        roles,
        organSpecializations,
        baseLocation: baseLocationObject,
        canDetach: true,
        assigned: employeesToAssign[selectedRole]?.includes(id) || false,
      };
    },
  );
};

export const getResourcesModalState = (filters: ResourceFilters, isHubSelected: boolean) => {
  const tmdxFilterSelected = resourceTypeIs(FilterResourceType.TmdxContacts, filters);

  const checks = [
    {
      type: ActiveResourceType.TmdxUser,
      match: tmdxFilterSelected && !!filters.tmdxUser.length && !candidatesWithScheduler.includes(filters.tmdxUser[0]),
      selectedRole: filters.tmdxUser[0],
    },
    {
      type: ActiveResourceType.TxCenterContact,
      match: resourceTypeIs(FilterResourceType.TXContacts, filters) && !!filters.txCenterContact.length,
      selectedRole: filters.txCenterContact[0],
    },
    {
      type: ActiveResourceType.Device,
      match: resourceTypeIs(FilterResourceType.Devices, filters) && isHubSelected,
    },
    {
      type: ActiveResourceType.OpoContact,
      match: resourceTypeIs(FilterResourceType.OpoContacts, filters),
    },
    {
      type: ActiveResourceType.ClinicalSpecialist,
      match: tmdxFilterSelected && filters.tmdxUser[0] === EmployeeRole.ClinicalSpecialist && isHubSelected,
      selectedRole: filters.tmdxUser[0],
    },
    {
      type: ActiveResourceType.RetrievalSurgeon,
      match: tmdxFilterSelected && filters.tmdxUser[0] === EmployeeRole.RetrievalSurgeon,
      selectedRole: filters.tmdxUser[0],
    },
    {
      type: ActiveResourceType.ClinicalCoordinator,
      match: tmdxFilterSelected && filters.tmdxUser[0] === EmployeeRole.ClinicalCoordinator,
      selectedRole: filters.tmdxUser[0],
    },
    {
      type: ActiveResourceType.ClinicalLead,
      match: tmdxFilterSelected && filters.tmdxUser[0] === EmployeeRole.ClinicalLead,
      selectedRole: filters.tmdxUser[0],
    },
    {
      type: ActiveResourceType.OrganManagementSpecialist,
      match: tmdxFilterSelected && filters.tmdxUser[0] === EmployeeRole.OrganManagementSpecialist,
      selectedRole: filters.tmdxUser[0],
    },
  ];

  const found = checks.find((c) => c.match);
  const resourceType = found ? found.type : ActiveResourceType.None;
  const selectedRole = found?.selectedRole;
  const showResourcesList = resourceType !== ActiveResourceType.None;

  return {
    resourceType,
    selectedRole,
    showResourcesList,
  };
};

const doneLogisticsFilter = (item: CaseResourceCombined) =>
  ![ResourceLogisticsStatus.Done, ResourceLogisticsStatus.OCSDone].includes(item.logisticsStatus);

const mapResourcesByType = (
  resourceGroups: Partial<Record<CaseResourceType, CaseResourceCombined[]>>,
  resourceType: CaseResourceType,
) => {
  return resourceGroups[resourceType]?.filter(doneLogisticsFilter).map((r) => r.resourceId) ?? [];
};

export const getAssignedResources = (
  assignedCustomers: AssignedUsersByGroup,
  assignedEmployees: AssignedUsersByGroup,
  assignedOpoContacts: AssignedOpoContactType[],
  resources: CaseResourceCombined[],
) => {
  const resourceGroups = groupBy(resources, 'resourceType');
  const assignedUsersByRoles = mapUsersIdsByRoles(assignedEmployees, assignedCustomers);
  const assignedContacts = assignedOpoContacts?.map((u) => ({ opoContactId: u.id, isPrimary: u.isPrimary })) ?? [];
  const clinicalSpecialistIds = mapResourcesByType(resourceGroups, CaseResourceType.ClinicalSpecialist);
  const surgeonIds = mapResourcesByType(resourceGroups, CaseResourceType.Surgeon);
  const ocsConsoles = resourceGroups[CaseResourceType.OCSConsole]?.filter(doneLogisticsFilter) || [];

  return {
    assignedUsersByRoles,
    assignedContacts,
    clinicalSpecialistIds,
    surgeonIds,
    ocsConsoles,
  };
};

export const getEmptyStateLabel = (filters: ResourceFilters, isHubSelected: boolean) => {
  if (
    (resourceTypeIs(FilterResourceType.Devices, filters) || filters.tmdxUser[0] === EmployeeRole.ClinicalSpecialist) &&
    !isHubSelected
  ) {
    return 'No NOP hub specified';
  }
  if (
    (resourceTypeIs(FilterResourceType.TmdxContacts, filters) && !filters.tmdxUser[0]) ||
    (resourceTypeIs(FilterResourceType.TXContacts, filters) && !filters.txCenterContact[0])
  ) {
    return 'No user role specified';
  }
  return 'No resources type specified';
};
